/**
 * The people whose protection rides on a member's subscription, and what the member reads before
 * ending it. One module, so hive (turning auto-renew off, asking for a refund) and checkout
 * (changing the plan to Essentials) name the same people in the same words, in the order app-api
 * lists them.
 *
 * The words promise nothing about what follows for those people beyond losing the protection: no
 * period of grace and no transition. The question is whether to take it from them.
 */

import { maskedPersonNames } from '@privacy-bee/format/text';

/**
 * Someone protected through the member's subscription now, as `GET /v3/me/license-gifts` lists
 * them under `dependents`, first to accept first.
 */
export interface GiftDependent {
    /** The first name they accepted with. Null, or blank, when their account had none at the time. */
    first_name: string | null;
    /** The address they accepted with, masked. Null when their account had none at the time. */
    masked_email: string | null;
    /** When they accepted, ISO-8601: the day their protection through the member began. */
    accepted_at: string;
}

/** The plans whose holder extends licenses to others, and so the plans other people's protection rides on. */
const SPONSORING_PLANS: readonly string[] = ['pro', 'signature'];

/** Whether a seat on this plan extends licenses to others. */
export function isSponsoringPlan(slug: string): boolean {
    return SPONSORING_PLANS.includes(slug);
}

/**
 * Whether the member's gifts ride on this seat: a live Pro or Signature seat the member holds. A seat
 * they bought for someone else carries that person's gifts, never theirs, and with no member known
 * there is no one whose gifts it could carry.
 */
export function carriesGifts(
    seat: { slug: string; expired: boolean; assigned_to: { id: number } | null },
    memberId: number | undefined,
): boolean {
    return !seat.expired
        && memberId !== undefined
        && seat.assigned_to?.id === memberId
        && isSponsoringPlan(seat.slug);
}

/** How many people a title names: the first to accept, who are usually the closest. */
const NAMED_IN_TITLE = 3;

const NAME_LIST = new Intl.ListFormat('en-US', { style: 'long', type: 'conjunction' });

/**
 * What a sentence calls the one person, when the list is exactly one person: their first name, or
 * the masked address they accepted with when they gave no name. Null with more than one person, or
 * one who accepted with neither.
 */
function soleLabel(dependents: readonly GiftDependent[]): string | null {
    const [only] = dependents;

    return dependents.length === 1 && only ? maskedPersonNames(only).label : null;
}

/**
 * The people a title names, in the order given, first to accept first, three at most, since the
 * list under the title names everyone: "Jim, Pam, and Dwight". Someone who accepted without a first
 * name is left out, since the list shows their address and a title never makes an address
 * possessive. Null with no name to use at all.
 */
function namedInTitle(dependents: readonly GiftDependent[]): string | null {
    const named = dependents
        .map((dependent) => maskedPersonNames(dependent).name)
        .filter((name): name is string => name !== null)
        .slice(0, NAMED_IN_TITLE);

    return named.length === 0 ? null : NAME_LIST.format(named);
}

/** "Revoke Jim, Pam, and Dwight's protection?": the question a dialog asks before the protection ends. */
export function revokeQuestion(dependents: readonly GiftDependent[]): string {
    const names = namedInTitle(dependents);

    return names ? `Revoke ${names}'s protection?` : 'Revoke their protection?';
}

/**
 * "Revoking Jim, Pam, and Dwight's protection": what a review states before the member confirms the
 * step that ends it, beside the acknowledgement they tick.
 */
export function revokingStatement(dependents: readonly GiftDependent[]): string {
    const names = namedInTitle(dependents);

    return names ? `Revoking ${names}'s protection` : 'Revoking their protection';
}

/** How the member's subscription stops carrying them: auto-renew turned off, or a change to Essentials. */
export type SponsorshipEnding = 'renewal-off' | 'downgrade';

const ENDING_CONDITION: Record<SponsorshipEnding, string> = {
    'renewal-off': "it doesn't renew",
    downgrade: 'it changes to Essentials',
};

/**
 * What the ending does to them, said before the list that names them: one person by name, or by the
 * address they accepted with when they gave no name; more by their number.
 */
export function dependentsLead(dependents: readonly GiftDependent[], ending: SponsorshipEnding): string {
    const condition = ENDING_CONDITION[ending];
    const name = soleLabel(dependents);

    if (name) return `${name} is protected through your subscription. If ${condition}, ${name} loses that protection.`;
    if (dependents.length === 1) return `One person is protected through your subscription. If ${condition}, they lose that protection.`;

    return `These ${dependents.length} people are protected through your subscription. If ${condition}, they lose that protection.`;
}

/**
 * The weighted answer: keeping the one person by name, or by address when they gave no name; more
 * people, or one with neither, as "them".
 */
export function keepProtectingLabel(dependents: readonly GiftDependent[]): string {
    const name = soleLabel(dependents);

    return name ? `Keep protecting ${name}` : 'Keep protecting them';
}

/**
 * How long, in milliseconds, a member waits on the read of who depends on their plan before it
 * counts as failed (`DEPENDENTS_UNREAD`). The step they are taking waits on that read, so it is bound
 * well inside each app's own request bounds.
 */
export const DEPENDENTS_READ_TIMEOUT_MS = 5_000;

/**
 * What a step says when who depends on the member's plan couldn't be read, before it asks the
 * member to try again.
 */
export const DEPENDENTS_UNREAD = "We couldn't check who depends on this plan";

/** What ending the subscription costs the people on it, concretely. */
export const DEPENDENTS_LOSE =
    "Their scans and removals stop, and data brokers start listing their home addresses and phone numbers again. That's how scammers, stalkers, doxxers, and telemarketers find them.";
